import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { adminUpdateAliases, getDrink } from "../../lib/api";
import type { SessionUser } from "../../types/auth";

/**
 * Aliases editor — owner-only. Search aliases tune what bartenders can type
 * to find a drink ("of" → Old Fashioned, "lou" → Caribou Lou). Lowercase +
 * dedupe + trim happens server-side; we surface the cleaned list on save.
 *
 * Route: /admin/recipes/:slug/aliases
 */

export default function AdminAliases() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { user } = useOutletContext<{ user: SessionUser }>();
  const isOwner = user.bar_roles.includes("bar_owner");

  const [drinkName, setDrinkName] = useState<string>("");
  const [drinkId, setDrinkId] = useState<string>("");
  const [aliases, setAliases] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  async function reload() {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const d = await getDrink(slug);
      setDrinkName(d.drink.name);
      setDrinkId(d.drink.id);
      setAliases(d.aliases ?? []);
    } catch (err: any) {
      setError(err?.message ?? "Could not load drink");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    reload();
  }, [slug]);

  function add() {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (aliases.includes(v)) {
      setDraft("");
      return;
    }
    setAliases([...aliases, v]);
    setDraft("");
  }
  function remove(idx: number) {
    setAliases(aliases.filter((_, i) => i !== idx));
  }

  async function save() {
    if (!drinkId) return;
    setSaving(true);
    setError(null);
    try {
      const r = await adminUpdateAliases(drinkId, aliases);
      setAliases(r.aliases);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (err: any) {
      setError(err?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!isOwner) {
    return (
      <section style={styles.shell}>
        <p style={styles.eyebrow}>Aliases</p>
        <h1 style={styles.title}>{drinkName || slug}</h1>
        {/* Static "you don't have access" notice — no role="alert" since
            it's there from page load, not a just-fired event. */}
        <div style={styles.lockedNote}>
          Aliases are <strong>bar_owner only</strong>. Ask Travis to update them.
        </div>
      </section>
    );
  }

  return (
    <section style={styles.shell}>
      <button
        type="button"
        onClick={() => navigate(`/admin/recipes/${slug}`)}
        style={styles.backLink}
      >
        ← Back to recipe
      </button>
      <p style={styles.eyebrow}>Search aliases</p>
      <h1 style={styles.title}>{drinkName || "(loading…)"}</h1>
      <p style={styles.body}>
        Aliases are extra search terms that find this drink. Bartenders type
        shorthand during service — add what they'd actually type.
        Examples for an Old Fashioned: <code>of</code>, <code>old fash</code>,
        <code> sentinel of</code>. Lowercase + duplicates removed automatically.
      </p>

      {loading && <p style={styles.muted}>Loading…</p>}
      {error && <div style={styles.errorBox} role="alert">{error}</div>}

      {!loading && (
        <>
          <ol style={styles.list}>
            {aliases.map((a, i) => (
              <li key={`${a}-${i}`} style={styles.row}>
                <code style={styles.code}>{a}</code>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  disabled={saving}
                  style={styles.removeBtn}
                  aria-label={`Remove alias ${a}`}
                >
                  ✕
                </button>
              </li>
            ))}
            {aliases.length === 0 && (
              <li style={{ ...styles.row, ...styles.empty }}>
                No aliases yet. Add the first one below.
              </li>
            )}
          </ol>

          <div style={styles.addRow}>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder="of"
              aria-label="New alias (e.g. of, old fash)"
              style={styles.input}
              disabled={saving}
            />
            <button type="button" onClick={add} style={styles.addBtn} disabled={saving}>
              Add
            </button>
          </div>

          <div style={styles.footer}>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={saving ? styles.saveBtnBusy : styles.saveBtn}
            >
              {saving ? "Saving…" : savedAt ? "✓ Saved" : "Save aliases"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

const styles: Record<string, CSSProperties> = {
  shell: { maxWidth: 720, padding: "8px 0 32px" },
  backLink: {
    background: "transparent",
    border: 0,
    color: "var(--muted)",
    fontWeight: 700,
    fontSize: 13,
    padding: "8px 0",
    cursor: "pointer",
    minHeight: 36,
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    margin: "8px 0 12px",
    letterSpacing: -0.4,
    color: "var(--text)",
  },
  body: { color: "var(--muted)", fontSize: 13, lineHeight: 1.5, margin: "0 0 16px" },
  muted: { color: "var(--muted)", fontSize: 13 },
  lockedNote: {
    background: "rgba(96,165,250,0.08)",
    border: "1px solid rgba(96,165,250,0.3)",
    color: "#93c5fd",
    borderRadius: 10,
    padding: 12,
    fontSize: 13,
  },
  errorBox: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  list: { listStyle: "none", padding: 0, margin: "0 0 12px", display: "grid", gap: 6 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    minHeight: 44,
  },
  code: {
    flex: 1,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    color: "var(--text)",
    fontSize: 13,
  },
  removeBtn: {
    width: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 8,
    color: "#FF8A7A",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  empty: { color: "var(--muted)", fontStyle: "italic", justifyContent: "center" },
  addRow: { display: "flex", gap: 8, marginBottom: 16 },
  input: {
    flex: 1,
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 10px",
    fontSize: 13,
    fontFamily: "inherit",
    minHeight: 40,
  },
  addBtn: {
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  footer: { display: "flex", justifyContent: "flex-end" },
  saveBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "10px 18px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  saveBtnBusy: {
    background: "rgba(249,175,54,0.4)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "10px 18px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "wait",
    minHeight: 40,
  },
};
